<template>
  <div class="area-form-fields">
    <v-row dense>
      <v-col cols="12" :sm="dense ? 12 : 4">
        <v-text-field
          :value="item.cod_area"
          @input="set('cod_area', $event)"
          :label="$t('area.code')"
          :rules="[regraCodigo]"
          counter="30"
          :dense="dense"
          :outlined="dense"
        />
      </v-col>
      <v-col cols="12" :sm="dense ? 12 : 8">
        <v-text-field
          :value="item.nome_area"
          @input="set('nome_area', $event)"
          :label="$t('area.name')"
          :rules="[regraNome]"
          counter="100"
          :dense="dense"
          :outlined="dense"
        />
      </v-col>
    </v-row>
    <v-row dense align="center">
      <v-col cols="12" :sm="dense ? 12 : 6">
        <v-switch
          :input-value="item.zona_risco"
          @change="set('zona_risco', $event)"
          :true-value="1"
          :false-value="0"
          color="red darken-2"
          class="mt-0"
          hide-details
        >
          <template v-slot:label>
            <v-icon small color="red darken-2" class="mr-1">{{ iconeZonaRisco }}</v-icon>
            {{ $t('area.riskZone') }}
          </template>
        </v-switch>
        <div class="caption grey--text mt-1">{{ $t('area.riskZoneHint') }}</div>
      </v-col>
      <v-col cols="12" :sm="dense ? 12 : 3">
        <v-switch :input-value="item.ativa"
          @change="set('ativa', $event)" :true-value="1" :false-value="0" :label="$t('area.active')" class="mt-0" hide-details />
      </v-col>
      <v-col v-if="Number(item.zona_risco) !== 1" cols="12" :sm="dense ? 12 : 3">
        <div class="caption grey--text">{{ $t('area.color') }}</div>
        <div class="d-flex flex-wrap">
          <v-btn
            v-for="c in cores"
            :key="c"
            x-small
            fab
            depressed
            class="mr-1 mb-1 area-form-fields__cor"
            :class="{ 'area-form-fields__cor--ativa': (item.cor || cores[0]) === c }"
            :color="c"
            @click="set('cor', c)"
          />
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <div class="d-flex align-center flex-wrap">
          <v-btn small class="mr-2 mb-1" :color="desenhando ? 'warning' : 'primary'" dark @click="$emit('toggle-draw')">
            <v-icon left small>mdi-vector-polygon</v-icon>
            {{ vertices ? $t('area.map.redraw') : $t('area.map.draw') }}
          </v-btn>
          <v-chip v-if="vertices > 0" x-small class="mr-2 mb-1" color="primary" text-color="white">
            {{ $t('area.map.polygonDefined', { count: vertices }) }}
          </v-chip>
          <v-btn v-if="vertices > 0" x-small text class="mb-1" @click="$emit('clear-polygon')">
            {{ $t('button.clear') }}
          </v-btn>
        </div>
        <div v-if="mostrarErroPoligono && !vertices" class="caption red--text">
          {{ $t('area.validation.polygonRequired') }}
        </div>
        <div class="d-flex align-center flex-wrap mt-2">
          <v-btn
            small
            outlined
            color="primary"
            class="mr-2 mb-1"
            :disabled="!unidadesComCentro"
            :loading="gerandoArea"
            @click="$emit('suggest-from-units', margem)"
          >
            <v-icon left small>mdi-vector-square-plus</v-icon>
            {{ $t('area.map.fromUnits') }}
          </v-btn>
          <v-text-field
            v-model.number="margem"
            type="number"
            min="10"
            max="50000"
            step="50"
            suffix="m"
            :label="$t('area.map.fromUnitsMargin')"
            :rules="[regraMargem]"
            class="area-form-fields__margem mb-1"
            dense
            outlined
            hide-details="auto"
          />
        </div>
        <div class="caption grey--text">
          {{ unidadesComCentro ? $t('area.map.fromUnitsHint') : $t('area.map.fromUnitsNeedUnits') }}
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" :sm="dense ? 12 : 6">
        <v-autocomplete
          :value="item.id_empresa"
          @input="set('id_empresa', $event)"
          :items="empresas"
          item-text="razao_social"
          item-value="id_empresa"
          clearable
          :label="$t('area.companyOptional')"
          :dense="dense"
          :outlined="dense"
        />
      </v-col>
      <v-col cols="6" :sm="dense ? 6 : 3">
        <v-switch
          :input-value="item.alarme_entrada"
          @change="set('alarme_entrada', $event)"
          :true-value="1"
          :false-value="0"
          :label="$t('area.entryAlarm')"
          class="mt-0"
          hide-details
        />
      </v-col>
      <v-col cols="6" :sm="dense ? 6 : 3">
        <v-switch
          :input-value="item.alarme_saida"
          @change="set('alarme_saida', $event)"
          :true-value="1"
          :false-value="0"
          :label="$t('area.exitAlarm')"
          class="mt-0"
          hide-details
        />
      </v-col>
    </v-row>
    <v-textarea
      :value="item.observacoes"
          @input="set('observacoes', $event)"
      :label="$t('area.observations')"
      rows="2"
      auto-grow
      clearable
      counter="15000"
      :rules="[(v) => !v || v.length <= 15000 || $t('area.validation.observationsTooLong')]"
      :dense="dense"
      :outlined="dense"
    />
  </div>
</template>

<script>
import { ICONE_ZONA_RISCO, COR_AREA_PADRAO } from "@/utils/areasMapa";
import { MARGEM_AREA_PADRAO_M } from "@/utils/areasGeometria";

/** Campos do cadastro de Área de Planejamento (parâmetros e drawer de criação). */
export default {
  name: "AreaFormFields",
  props: {
    item: { type: Object, required: true },
    empresas: { type: Array, default: () => [] },
    vertices: { type: Number, default: 0 },
    desenhando: { type: Boolean, default: false },
    mostrarErroPoligono: { type: Boolean, default: false },
    /** Unidades associadas com centro: habilita gerar a área a partir delas. */
    unidadesComCentro: { type: Number, default: 0 },
    gerandoArea: { type: Boolean, default: false },
    dense: { type: Boolean, default: false },
  },
  data: () => ({
    iconeZonaRisco: ICONE_ZONA_RISCO,
    margem: MARGEM_AREA_PADRAO_M,
    cores: [COR_AREA_PADRAO, "#2E7D32", "#6A1B9A", "#EF6C00", "#00838F", "#5D4037"],
  }),
  methods: {
    set(campo, valor) {
      this.$emit("set", campo, valor);
    },
    regraCodigo(v) {
      const t = v ? String(v).trim() : "";
      if (!t || t.length > 30 || !/^[A-Za-z0-9_.-]+$/.test(t)) return this.$t("area.codeRequired");
      return true;
    },
    regraMargem(v) {
      const n = Number(v);
      return (Number.isFinite(n) && n >= 10 && n <= 50000) || this.$t('area.map.fromUnitsMarginInvalid');
    },
    regraNome(v) {
      const t = v ? String(v).trim() : "";
      if (!t || t.length > 100) return this.$t("area.nameRequired");
      return true;
    },
  },
};
</script>

<style scoped>
.area-form-fields__cor {
  width: 22px !important;
  height: 22px !important;
}
.area-form-fields__margem {
  max-width: 140px;
}
.area-form-fields__cor--ativa {
  outline: 2px solid rgba(0, 0, 0, 0.6);
  outline-offset: 1px;
}
</style>
